import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Link as MuiLink,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { ToastService } from "src/Shared/Services";
import DateTimeUtils from "src/Shared/Tools/DateTimeUtils";
import ActionModal from "src/Shared/Components/ActionModal/ActionModal";
import CompanyService from "../../Services/Company.service";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import { ClientStatus } from "../../Enums/ClientStatus.enum";
import { IOnboardingWizardState } from "../../Interfaces/IOnboardingWizardState";
import { IOnboardingSubmissionPlan } from "../../Interfaces/IOnboardingOutputs";
import { downloadJsonFile } from "../../Tools/OnboardingOutputs";
import { countryLabel, referenceLabel, xeroKeyAllocationSummary } from "./OnboardingReview.labels";
import { contactFlagTags, notificationSummary } from "../../Tools/ContactUtils";
import { TESTING_OCCASIONS } from "../../Enums/TestingOccasion.enum";
import {
  areCaptureStepsComplete,
  canSubmitOnboarding,
  emDashWhenBlank,
  reviewSections,
  showsHealthTick,
  showsTdda,
  siteAnswerLabel,
  submitBlockedReason,
} from "./OnboardingWizard.utils";
import {
  activeServices,
  COLLECTION_COLLECT_ONLY,
  derivedSiteLabs,
  isLabChosenByClient,
  METHOD_ALCOHOL,
} from "./Step2/Step2.rules";
import { IOnboardingImportRequest } from "../../Interfaces/IOnboardingOutputs";
import { useImperansReferenceList } from "../ImperansReference/useImperansReferenceList";
import { isPickStale } from "../ImperansReference/ImperansReference.rules";
import ImperansSubmissionResult from "./ImperansSubmissionResult";
import {
  AWAITS_IMPERANS_ACTIVATION_MESSAGE,
  awaitsImperansActivation,
  changedNothing,
} from "./OnboardingSubmission.rules";
import { showsHealthTickPurchaseOrder } from "./OnboardingCapture.utils";
import { IImperansSubmission } from "../../Interfaces/IImperansSubmission";
import { ImperansSubmissionOutcome } from "../../Enums/ImperansSubmissionOutcome.enum";

/** Appended to a franchise or clinic Imperans no longer offers (story 6374). */
export const STALE_ON_REVIEW = "no longer offered by Imperans — pick again";
export const PICKS_UNCHECKED_ON_REVIEW =
  "Imperans could not be reached, so the franchise and clinic picks were not re-checked.";

type Props = {
  state: IOnboardingWizardState;
  companyRowID: string;
  onGoToStep: (step: number) => void;
  onChanged: () => void;
};

export const CAPTURE_CONFIRMATION_LABEL =
  "I confirm this information has been captured with the client and is correct.";

/**
 * The message CompanyAppService.SubmitOnboarding returns on a repeat click. The delivery sequence
 * skips it, so the export is only ever written for a submission that just happened (S7.6, bug
 * 5686). Reword it on the server and this must change with it.
 */
const ALREADY_SUBMITTED_MESSAGE = "Onboarding has already been submitted.";

/** Submit delivers the outputs once Imperans has the client, or when the CRM records it alone (story 6342). */
const deliversOutputs = (result: IImperansSubmission | null): boolean =>
  Boolean(result && (result.isCreated || result.outcome === ImperansSubmissionOutcome.KeptInCrmOnly));

/**
 * `emptyText` overrides the default em dash for a field that is absent rather than not applicable —
 * "Not provided." rather than "—" (task 5868's convention: an em dash reads as "nothing to show",
 * which is the wrong message for a fact nobody has captured yet).
 */
const Value: React.FC<{ label: string; value: any; emptyText?: string }> = ({
  label,
  value,
  emptyText,
}) => (
  <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">
      {emptyText && (value === null || value === undefined || `${value}`.trim() === "")
        ? emptyText
        : emDashWhenBlank(value)}
    </Typography>
  </Grid>
);

/**
 * Step 7 — Review &amp; Submit (stories 5593, 5594, 5595).
 *
 * All seven sections render in fixed order for every client regardless of service line — the same
 * order the SOP prints in — each with an Edit link into the step that owns it, and each showing its
 * explicit empty line rather than disappearing. Every value is read from the stored payload;
 * nothing here is re-typed or separately stored.
 *
 * Submit is gated on the same shared completion rules as every other progress reader, on the
 * signed-in user being the primary relationship manager, and on the capture confirmation.
 */
const OnboardingReviewStep: React.FC<Props> = ({
  state,
  companyRowID,
  onGoToStep,
  onChanged,
}) => {
  const refLabel = referenceLabel;

  const [captureConfirmed, setCaptureConfirmed] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [planOpen, setPlanOpen] = useState<boolean>(false);
  const [plan, setPlan] = useState<IOnboardingSubmissionPlan | null>(null);
  const [promoteOpen, setPromoteOpen] = useState<boolean>(false);
  const [promoting, setPromoting] = useState<boolean>(false);
  const navigate = useNavigate();
  const {
    rootStore: {
      companyStore: { resetHomeGridState },
    },
  } = useStore();

  const frozen = state.isSubmitted;
  const sections = reviewSections(state);
  const complete = areCaptureStepsComplete(state);

  // Where the latest submission stands with Imperans (Epic 6303, story 6339): what the server
  // holds on load, then whatever Submit or Try again just answered.
  const [submission, setSubmission] = useState<IImperansSubmission | null>(state.imperansSubmission ?? null);
  const [retrying, setRetrying] = useState<boolean>(false);
  useEffect(() => {
    setSubmission(state.imperansSubmission ?? null);
  }, [state.imperansSubmission]);

  // The paired Imperans's lists, fetched fresh so a franchise or clinic deactivated since it was
  // picked is flagged here as well as on its step (story 6374). A frozen record is not re-checked:
  // it is the record of what was sent.
  const franchiseList = useImperansReferenceList("franchises", state.countryCode, !frozen);
  const clinicList = useImperansReferenceList("clinics", state.countryCode, !frozen);
  const franchiseStale = isPickStale(state.imperansFranchiseID, franchiseList.loaded ? franchiseList.options : null);
  const clinicStale = (id: number | null | undefined) =>
    isPickStale(id, clinicList.loaded ? clinicList.options : null);
  const picksUnchecked = !frozen && (franchiseList.unavailable || clinicList.unavailable);

  const onAcknowledgementChange = (
    field: "termsAcknowledged" | "marketingOptIn",
    value: boolean,
  ) => {
    CompanyService.saveOnboardingAcknowledgements({
      companyRowID,
      [field]: value,
    }).subscribe({
      next: () => onChanged(),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  /**
   * The delivery order S7.6 fixes: the record is stamped first, the JSON downloads second, and the
   * SOP opens third with no further click — so the downloaded file always carries the submission
   * stamp and nobody downstream is surprised.
   */
  /**
   * Steps 2 and 3 of the delivery order S7.6 fixes: the export regenerates from the stamped record,
   * then the SOP opens. The blank tab was claimed synchronously inside the click that started the
   * sequence (bug 5687); if the browser refused it, the SOP opens in this tab instead.
   */
  const deliverOutputs = (sopWindow: Window | null) => {
    const sopUrl = `/company/${companyRowID}/onboarding/sop`;
    CompanyService.getOnboardingExport(companyRowID).subscribe({
      next: (exported) => {
        downloadJsonFile(exported.fileName, exported.json);
        if (sopWindow && !sopWindow.closed) sopWindow.location.href = sopUrl;
        else window.location.assign(sopUrl);
      },
      error: (error: AxiosError) => {
        sopWindow?.close();
        ToastService.axiosError(error);
      },
    });
  };

  const submit = () => {
    setSubmitting(true);
    setPlanOpen(false);

    // The tab is claimed here, synchronously inside the confirm click, because a window.open issued
    // later from a nested subscribe sits outside the user-gesture stack and browsers block it — which
    // is why step 3 of the promised sequence never happened (bug 5687). It is closed again unless
    // Imperans created the client (Epic 6303, story 6342).
    const sopWindow = window.open("", "_blank");

    CompanyService.submitOnboarding(companyRowID, captureConfirmed).subscribe({
      next: (response) => {
        setSubmitting(false);
        const result = (response.content as IImperansSubmission | null) ?? null;
        setSubmission(result);

        // Only a submission that actually happened delivers the outputs. A repeat click answers
        // "Onboarding has already been submitted." and must not download the export again (bug 5686).
        // A rejection, a refusal or a fault delivers nothing: the panel below says what happened.
        const fresh = response.message !== ALREADY_SUBMITTED_MESSAGE;
        if (fresh && deliversOutputs(result)) {
          ToastService.success(result?.isCreated ? "Created in Imperans as Draft." : response.message);
          deliverOutputs(sopWindow);
        } else {
          sopWindow?.close();
          if (result && !result.isCreated) ToastService.error(result.message || response.message);
        }

        // A self-check stop wrote nothing; reloading would swap its findings for an older answer.
        if (!changedNothing(result)) onChanged();
      },
      error: (error: AxiosError) => {
        setSubmitting(false);
        sopWindow?.close();
        ToastService.axiosError(error);
      },
    });
  };

  /** Try again with the same key and body after a timeout, fault or refusal (story 6338). */
  const retry = () => {
    setRetrying(true);
    const sopWindow = window.open("", "_blank");
    CompanyService.retryImperansSubmission(companyRowID).subscribe({
      next: (response) => {
        setRetrying(false);
        setSubmission(response.content ?? null);
        if (response.content?.isCreated) {
          ToastService.success("Created in Imperans as Draft.");
          deliverOutputs(sopWindow);
        } else {
          sopWindow?.close();
          ToastService.error(response.content?.message || response.message);
        }
        onChanged();
      },
      error: (error: AxiosError) => {
        setRetrying(false);
        sopWindow?.close();
        ToastService.axiosError(error);
      },
    });
  };

  /** The shared cross-system modal lists what submission will do before anything is written (S7.7). */
  const openSubmissionPlan = () => {
    CompanyService.getOnboardingSubmissionPlan(companyRowID).subscribe({
      next: (data) => {
        setPlan(data);
        setPlanOpen(true);
      },
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  const onDownloadJson = () => {
    CompanyService.getOnboardingExport(companyRowID).subscribe({
      next: (exported) => downloadJsonFile(exported.fileName, exported.json),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  /**
   * Builds the schemaVersion 2 Imperans import request from the stored onboarding and shows the
   * CRM's own self-check of it (Epic 6303 Feature CRM2, stories 6346, 6347). Nothing is sent and
   * nothing is written: Feature CRM1 sends exactly this body on submit, so seeing it beforehand is
   * how a request that would be rejected is caught before anything leaves the CRM.
   */
  const [importCheck, setImportCheck] = useState<IOnboardingImportRequest | null>(null);
  const onCheckImperansRequest = () => {
    // The capture tick is only stored on submit, so the check is told what is ticked on screen.
    CompanyService.getOnboardingImportRequest(companyRowID, captureConfirmed).subscribe({
      next: (result) => setImportCheck(result),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  const importCheckPanel = importCheck && (
    <Paper elevation={0} sx={{ p: 2, mt: 2, border: "1px solid #e0e0e0" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Imperans import request (schemaVersion 2)
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" onClick={() => downloadJsonFile(importCheck.fileName, importCheck.json)}>
            Download request JSON
          </Button>
          <Button size="small" onClick={() => setImportCheck(null)}>
            Close
          </Button>
        </Box>
      </Box>
      {importCheck.isReady ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          Every self-check passed. This is the request Imperans will receive on submit.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {importCheck.selfCheck.length} item{importCheck.selfCheck.length === 1 ? "" : "s"} would
            be rejected by Imperans. Each names the step that fixes it.
          </Typography>
          {importCheck.selfCheck.map((issue, index) => (
            <Typography key={`${issue.check}-${issue.path}-${index}`} variant="body2">
              <strong>{issue.check}</strong>
              {issue.step ? (
                <>
                  {" · "}
                  <Link to="#" onClick={(e) => { e.preventDefault(); onGoToStep(issue.step!); }}>
                    Step {issue.step}
                  </Link>
                </>
              ) : (
                " · configuration"
              )}
              {" — "}
              {issue.message}
            </Typography>
          ))}
        </Alert>
      )}
    </Paper>
  );

  const renderSectionBody = (key: string, emptyLine: string) => {
    switch (key) {
      case "clientInformation":
        return (
          <Grid container spacing={2}>
            <Value label="Company Name" value={state.companyName} />
            <Value label="Company Code" value={state.companyCode} />
            {/* The label, not the code: this read "NZ" where step 1 reads "New Zealand"
                (bug 5717). */}
            <Value label="Country" value={countryLabel(state.countryCode)} />
            <Value label="Sub/Division Code" value={state.subDivisionCode} />
            <Value label="TDDA Supporting Region" value={state.region} />
            {/* The client-level supporting clinic left the onboarding with Feature CRM3 (story
                6373); the owning Imperans franchise is the pick from Imperans's list (story 6372). */}
            <Value
              label="Owning Imperans franchise"
              value={
                state.imperansFranchiseID
                  ? `${state.imperansFranchiseName ?? state.imperansFranchiseID}${
                      franchiseStale ? ` (${STALE_ON_REVIEW})` : ""
                    }`
                  : null
              }
              emptyText="Not picked."
            />
            {/* One Client Type since D12 (story 6348), mirrored to the CRM's National/Regional. */}
            <Value
              label="Client Type"
              value={state.clientInformation?.clientType || state.companyType}
            />
            <Value label="Head Office Phone" value={state.headOfficePhone} />
            <Value label="Industry" value={state.industry} />
            <Value label="Lead Type" value={state.leadType} />
            <Value label="Website" value={state.website} />
            <Value
              label="Total Employee Numbers"
              value={state.totalStaffNumber}
              emptyText="Not provided."
            />
            <Value
              label="TDDA Primary Relationship Manager"
              value={state.primaryRelationshipManager}
            />
            <Value label="Special Pricing" value={state.specialPricing ? "Yes" : "No"} />
          </Grid>
        );

      case "servicesTesting": {
        if (!state.services?.serviceLine) return <Body>{emptyLine}</Body>;

        const labChosenByClient = isLabChosenByClient(state.references.step2Context);
        const screened = state.services.urineEnabled || state.services.oralFluidEnabled;
        const byState = derivedSiteLabs(state.references.step2Context);
        // Named per state, because that is what decides it. An address the state cannot be read
        // from says so rather than leaving the row blank, which would read as "no laboratory".
        const screenedLabsByState = !screened
          ? null
          : byState.length
            ? `urine & oral fluid: ${byState
                .map((x) => `${refLabel(state.references.step2Laboratories, x.lab) || x.lab} (${x.state})`)
                .join(", ")}`
            : "urine & oral fluid: not yet derived — no site address gives a state";

        return (
          <>
            <Grid container spacing={2}>
              <Value label="Service line" value={state.services.serviceLine} />
            </Grid>
            {/* TDDA blocks show only when the service line includes TDDA, HealthTick blocks only
                when it includes HealthTick (S7.1). */}
            {/* TDDA blocks only when the service line includes TDDA (S7.1). */}
            {showsTdda(state) && (
              <Box sx={{ mt: 1 }}>
                <Grid container spacing={2}>
                  {/* The panel is per service since bug 6368 and prints on each service line below. */}
                  <Value
                    label="Confirmation laboratories"
                    value={[
                      // The laboratory's name, not its stored key: DHM reads "Douglass Hanly Moir
                      // (DHM)" in the picklist and printed as "DHM" here (bug 5717).
                      //
                      // Urine and oral fluid are one client-level choice in New Zealand and a
                      // per-office derivation in Australia, where a client may use all three at
                      // once — so there they are listed per state rather than per method, which
                      // is the same answer the SOP puts on each site row (task 6008). Hair and
                      // nail are fixed laboratories in both countries.
                      ...(labChosenByClient
                        ? [
                            state.services.urineEnabled
                              ? `urine: ${
                                  refLabel(
                                    state.references.step2Laboratories,
                                    state.services.urineLab,
                                  ) || "—"
                                }`
                              : null,
                            state.services.oralFluidEnabled
                              ? `oral fluid: ${
                                  refLabel(
                                    state.references.step2Laboratories,
                                    state.services.oralFluidLab,
                                  ) || "—"
                                }`
                              : null,
                          ]
                        : [screenedLabsByState]),
                      state.services.hairEnabled ? "hair: Omega" : null,
                      state.services.nailEnabled ? "nail: USDTL" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  />
                  <Value
                    label="Alcohol cut-offs"
                    value={
                      state.services.alcoholBandUnder20 || state.services.alcoholBand20Plus
                        ? `under 20: ${emDashWhenBlank(state.services.alcoholBandUnder20)} · 20+: ${emDashWhenBlank(state.services.alcoholBand20Plus)}`
                        : null
                    }
                  />
                  <Value label="Urgent setup" value={state.services.isUrgentSetup ? "Yes" : "No"} />
                </Grid>

                {screenedSummary(state).length ? (
                  screenedSummary(state).map((line) => (
                    <Typography key={line} variant="body2">
                      {line}
                    </Typography>
                  ))
                ) : (
                  <Body>No testing occasions included.</Body>
                )}

                {/* Every specialist service line prints separately beneath its shared reason (S2.7). */}
                {specialistSummary(state).map((line) => (
                  <Typography key={line} variant="body2">
                    {line}
                  </Typography>
                ))}

                {otherServicesSummary(state) && (
                  <Typography variant="body2">{otherServicesSummary(state)}</Typography>
                )}
              </Box>
            )}

            {showsHealthTick(state) && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  HealthTick services
                </Typography>
                {healthTickSummary(state).length ? (
                  healthTickSummary(state).map((line) => (
                    <Typography key={line} variant="body2">
                      {line}
                    </Typography>
                  ))
                ) : (
                  <Body>Not provided.</Body>
                )}
              </Box>
            )}
            {state.healthTickReview && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                HealthTick review{" "}
                {state.healthTickReview.status === 1 ? "pending" : "complete"}
                {state.healthTickReview.completedBy
                  ? ` — ${state.healthTickReview.completedBy}`
                  : ""}
              </Typography>
            )}
          </>
        );
      }

      case "sites":
        if (!state.sites.length) return <Body>{emptyLine}</Body>;
        return (
          <>
            {state.sites.map((site) => (
              <Typography key={site.companySiteID} variant="body2">
                {emDashWhenBlank(site.officeName)}
                {site.isPrimaryOffice ? " (head office)" : ""} —{" "}
                {emDashWhenBlank(site.officeFullAddress)}
                {/* The Imperans clinic servicing the site (story 6373), flagged once Imperans stops
                    offering it (story 6374). */}
                {" · "}
                {site.imperansFranchiseOfficeID
                  ? `Clinic: ${site.imperansFranchiseOfficeName ?? site.imperansFranchiseOfficeID}${
                      clinicStale(site.imperansFranchiseOfficeID) ? ` (${STALE_ON_REVIEW})` : ""
                    }`
                  : "No servicing clinic picked"}
              </Typography>
            ))}
            {picksUnchecked && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {PICKS_UNCHECKED_ON_REVIEW}
              </Typography>
            )}
          </>
        );

      case "contacts": {
        // Kept alongside the contacts, not gated on there being any: the mailbox and notes are the
        // onboarding's own settings, independent of who is captured as a contact (S4.4, bug 5828).
        const mailbox = state.contactSettings;
        const showsMailbox = Boolean(mailbox?.genericMailboxEnabled && mailbox?.genericMailboxAddress);
        const showsNotes = Boolean(mailbox?.distributionNotes?.trim());

        return (
          <>
            {state.contacts.length ? (
              state.contacts.map((contact) => (
                <Box key={contact.companyContactID} sx={{ mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                    <Typography variant="body2">
                      {emDashWhenBlank(contact.fullName)} — {emDashWhenBlank(contact.emailAddress)}
                    </Typography>
                    {/* Every flag as its own tag, not just Main/RTP/results access — a contact holding
                        Point of Contact or Safety Critical (visible as chips on the step-4 grid) used
                        to reach the review with neither (bug 5827). */}
                    {contactFlagTags(contact).map((tag) => (
                      <Chip key={tag} size="small" variant="outlined" label={tag} />
                    ))}
                  </Box>
                  {/* The per-contact notification scope, same wording as the grid's tooltip — absent
                      from the review entirely until now (bug 5828). Only meaningful for a contact
                      actually receiving results. */}
                  {contact.hasResultsAccess && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {notificationSummary(contact.notifications, TESTING_OCCASIONS, state.sites)}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Body>{emptyLine}</Body>
            )}

            {(showsMailbox || showsNotes) && (
              <Box sx={{ mt: 1 }}>
                {showsMailbox && (
                  <Typography variant="body2">
                    Shared mailbox: {mailbox!.genericMailboxAddress}
                  </Typography>
                )}
                {showsNotes && (
                  <Typography variant="body2">
                    Distribution notes: {mailbox!.distributionNotes}
                  </Typography>
                )}
              </Box>
            )}
          </>
        );
      }

      case "invoicing":
        return (
          <>
            <Grid container spacing={2}>
              {/* "HeadOfficeDirect" is a stored key, not something to print (bug 5717). */}
              <Value
                label="Arrangement"
                value={refLabel(
                  state.references.invoicingArrangements,
                  state.invoicing?.arrangement,
                )}
              />
              <Value label="Invoice email" value={state.invoicing?.invoiceEmail} />
              <Value
                label="Billing contact"
                value={state.invoicing?.billingContactName}
              />
              <Value
                label="Payment terms"
                value={
                  state.references.paymentTerms.find(
                    (x) => x.key === state.invoicing?.paymentTerms,
                  )?.label
                }
              />
              <Value label="Invoice delivery" value={state.invoicing?.deliveryMethod} />
              {/* The chosen labels print as one line (S5.3). */}
              <Value
                label="Details required on the invoice"
                value={state.references.invoiceDetailOptions
                  .filter((option) =>
                    (state.invoicing?.invoiceDetailOptions || []).includes(option.key),
                  )
                  .map((option) => option.label)
                  .join(", ")}
              />
              {/* The review stops showing the PO block when no PO is required (S5.5). */}
              {state.invoicing?.purchaseOrder?.required && (
                <>
                  <Value label="PO number" value={state.invoicing.purchaseOrder.number} />
                  {/* Only while the line includes HealthTick, as on step 5, the SOP and the
                      import request: a number kept from a Both line is not shown (S5.5). */}
                  {showsHealthTickPurchaseOrder(state) && (
                    <Value
                      label="HealthTick PO number"
                      value={state.invoicing.purchaseOrder.healthTickNumber}
                    />
                  )}
                  <Value
                    label="Invoices per PO"
                    value={state.invoicing.purchaseOrder.invoicesPerPurchaseOrder}
                  />
                  <Value
                    label="PO format / rules"
                    value={state.invoicing.purchaseOrder.formatRules}
                  />
                  <Value
                    label="PO contact"
                    value={state.invoicing.purchaseOrder.contactDetails}
                  />
                </>
              )}
            </Grid>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Xero keys
            </Typography>
            {!state.invoicing?.xeroKeys?.length ? (
              <Body>{emptyLine}</Body>
            ) : (
              state.invoicing.xeroKeys.map((key, index) => (
                <Typography key={`${key.name}-${index}`} variant="body2">
                  {emDashWhenBlank(key.name)} · {emDashWhenBlank(key.keyValue)} ·{" "}
                  {emDashWhenBlank(
                    refLabel(state.references.invoiceTimings, key.invoiceTiming),
                  )}{" "}
                  ·{" "}
                  {/* With a single key the review prints it as all services / all offices (S5.6). */}
                  {state.invoicing.xeroKeys.length === 1
                    ? "all services / all offices"
                    : xeroKeyAllocationSummary(key, state.references.serviceCategories, state.sites)}
                </Typography>
              ))
            )}
          </>
        );

      case "information": {
        const information = state.information;
        const anything =
          information &&
          (information.financeDetails || information.bookingDetails || information.occfNotes);
        if (!anything) return <Body>{emptyLine}</Body>;
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary" display="block">
                Finance Details
              </Typography>
              <SafeHtml html={information.financeDetails} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary" display="block">
                Booking Details
              </Typography>
              <SafeHtml html={information.bookingDetails} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block">
                OCCF Notes — internal, never client-visible
              </Typography>
              {/* Always plain text, wherever it renders (S6.8). */}
              {/* The three information slots read alike when empty: this one printed an em dash
                  while Booking Details printed "Not provided." (bug 5718). */}
              <Typography variant="body2">
                {information.occfNotes?.trim() ? information.occfNotes : "Not provided."}
              </Typography>
            </Grid>
          </Grid>
        );
      }

      case "siteRequirements": {
        const rtp = state.contacts.find((x) => x.isRequestingTestPersonnel);
        return (
          <>
            {state.siteQuestions.map((question) => (
              <Box
                key={question.key}
                sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}
              >
                <Typography variant="body2">{question.label}</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {siteAnswerLabel(state.siteRequirements?.answers?.[question.key])}
                </Typography>
              </Box>
            ))}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Value
                label="Site requirement notes"
                value={state.siteRequirements?.notes}
                emptyText="Not provided."
              />
              {/* No certificate number: the field is withdrawn (task 6010). */}
              <Value label="RTP contact" value={rtp?.fullName} />
              <Value label="RTP email" value={rtp?.emailAddress} />
              <Value label="RTP phone" value={rtp?.phoneNumber} />
            </Grid>
          </>
        );
      }

      default:
        return <Body>{emptyLine}</Body>;
    }
  };

  return (
    <>
      {/* Story 5594: the outstanding-step alert names each step as a deep link; with everything
          complete the success alert says so. */}
      {/* Once submitted it is a record, not a form: it read "ready to submit" after the event
          (bug 5723). The confirmation panel below carries the submission stamp instead. */}
      {state.isSubmitted ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          This onboarding has been submitted and is a point-in-time record.
        </Alert>
      ) : complete ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          All steps are complete and ready to submit.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Outstanding steps</AlertTitle>
          {state.outstandingSteps.map((outstanding) => (
            <Box key={outstanding.step}>
              <MuiLink
                component="button"
                type="button"
                underline="hover"
                onClick={() => onGoToStep(outstanding.step)}
              >
                Step {outstanding.step} — {outstanding.title}
              </MuiLink>
            </Box>
          ))}
        </Alert>
      )}

      {sections.map((section) => (
        <Paper
          key={section.key}
          elevation={0}
          sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {section.title}
              {section.count !== undefined ? ` (${section.count})` : ""}
            </Typography>
            {/* Edit links are absent from the submitted review screen and the SOP (S7.1). */}
            {!frozen && (
              <Button size="small" onClick={() => onGoToStep(section.ownerStep)}>
                Edit
              </Button>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />
          {renderSectionBody(section.key, section.emptyLine)}
        </Paper>
      ))}

      {frozen ? (
        // Story 5592/5595: the review form and Submit are replaced by the confirmation panel, and
        // the stored confirmations show who gave them and when.
        <Paper elevation={0} sx={{ p: 2, border: "1px solid #cfe3cf", backgroundColor: "#f6fbf6" }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Onboarding submitted
          </Typography>
          <Typography variant="body2">
            Submitted{" "}
            {state.submittedOn
              ? DateTimeUtils.dateToLocalTimeZone(state.submittedOn)
              : ""}
            {state.submittedBy ? ` by ${state.submittedBy}` : ""}.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {CAPTURE_CONFIRMATION_LABEL}{" "}
            <strong>
              {state.acknowledgements.captureConfirmedBy || "—"}
              {state.acknowledgements.captureConfirmedOn
                ? `, ${DateTimeUtils.dateToLocalTimeZone(state.acknowledgements.captureConfirmedOn)}`
                : ""}
            </strong>
          </Typography>
          <Typography variant="body2">
            Terms acknowledged:{" "}
            {state.acknowledgements.termsAcknowledged
              ? `${state.acknowledgements.termsAcknowledgedBy || "—"}${
                  state.acknowledgements.termsAcknowledgedOn
                    ? `, ${DateTimeUtils.dateToLocalTimeZone(state.acknowledgements.termsAcknowledgedOn)}`
                    : ""
                }`
              : "No"}
          </Typography>
          <Typography variant="body2">
            Marketing opt-in:{" "}
            {state.acknowledgements.marketingOptIn
              ? `${state.acknowledgements.marketingOptInBy || "—"}${
                  state.acknowledgements.marketingOptInOn
                    ? `, ${DateTimeUtils.dateToLocalTimeZone(state.acknowledgements.marketingOptInOn)}`
                    : ""
                }`
              : "No"}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            The SOP goes to{" "}
            <strong>{emDashWhenBlank(state.primaryRelationshipManager)}</strong>, this client's
            primary relationship manager.
          </Typography>

          {/* What Imperans answered (Epic 6303, story 6339), with Try again after a timeout, a
              fault or a refusal (story 6338). */}
          <ImperansSubmissionResult submission={submission} busy={retrying} onRetry={retry} />

          {/* The close-out actions (S7.10). Mark as Current Client disappears once it is one, and
              is not offered while Imperans holds the client as Draft: activation arrives from
              Imperans and moves the record itself (Feature CRM4). */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
            <Button
              variant="contained"
              component={Link}
              to={`/company/${companyRowID}/onboarding/sop`}
            >
              View / Print SOP Output
            </Button>
            <Button variant="outlined" onClick={onDownloadJson}>
              Download JSON
            </Button>
            <Button variant="outlined" onClick={onCheckImperansRequest}>
              Check Imperans request
            </Button>
            <Button component={Link} to={`/company/${companyRowID}/onboarding/setup-copy`}>
              Setup copy view
            </Button>
            {state.clientStatusID !== ClientStatus.CurrentClient && !awaitsImperansActivation(submission) && (
              <Button variant="outlined" onClick={() => setPromoteOpen(true)}>
                Mark as Current Client
              </Button>
            )}
            {state.clientStatusID !== ClientStatus.CurrentClient && awaitsImperansActivation(submission) && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                {AWAITS_IMPERANS_ACTIVATION_MESSAGE}
              </Typography>
            )}
            <Button component={Link} to={`/company/${companyRowID}/overview`}>
              Back to Client Record
            </Button>
          </Box>

          {/* Promotion sets status and the Imperans link together, then lands on Home with the row
              focused (Feature 1). */}
          <ActionModal
            open={promoteOpen}
            title="Mark as Current Client?"
            items={[
              "Set this client's status to Current Client.",
              "Mark the client as linked to Imperans.",
              "Return you to Home with this client's row highlighted.",
            ]}
            confirmLabel="Mark as Current Client"
            busy={promoting}
            onCancel={() => setPromoteOpen(false)}
            onConfirm={() => {
              setPromoting(true);
              CompanyService.markAsCurrentClient(companyRowID).subscribe({
                next: (response) => {
                  setPromoting(false);
                  setPromoteOpen(false);
                  ToastService.success(response.message);
                  // Home keeps its filter and page number on the store, so without this the
                  // promoted row can land on a page Home is not showing and the highlight this
                  // modal promises never appears (bug 5722).
                  resetHomeGridState();
                  navigate(`/company?focus=${companyRowID}`);
                },
                error: (error: AxiosError) => {
                  setPromoting(false);
                  ToastService.axiosError(error);
                },
              });
            }}
          />
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Confirm and submit
          </Typography>

          {/* Submit now creates the client in the paired Imperans and waits for its answer (Epic
              6303, story 6338), unless the client already carries an Imperans Link. */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {state.hasImperansLink
              ? "This client already has an Imperans Link, so submitting records the onboarding in the CRM only."
              : state.submitsToImperans
                ? "Submitting sends the onboarding to Imperans and waits for its answer: created as Draft, or rejected with every reason listed."
                : "The CRM is not paired with an Imperans API, so the onboarding cannot be sent yet."}
          </Typography>

          {/* A rejection, a refusal or a fault from the last attempt stays on screen while the
              onboarding is edited (stories 6339, 6340). */}
          <ImperansSubmissionResult submission={submission} busy={submitting && state.submitsToImperans} onGoToStep={onGoToStep} />

          {/* Each acknowledgement is a separate control, recorded independently of the others
              with its value, timestamp and confirming user (story 5595). */}
          <FormControlLabel
            control={
              <Checkbox
                checked={state.acknowledgements.termsAcknowledged}
                onChange={(e) =>
                  onAcknowledgementChange("termsAcknowledged", e.target.checked)
                }
              />
            }
            label="The client has acknowledged the terms and conditions."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={state.acknowledgements.marketingOptIn}
                onChange={(e) =>
                  onAcknowledgementChange("marketingOptIn", e.target.checked)
                }
              />
            }
            label="The client opts in to marketing communications."
          />

          <Divider sx={{ my: 1 }} />

          {/* The capture confirmation is given at the moment of submission; an un-submitted tick is
              never stored, so a reload clears it (story 5595). */}
          <FormControlLabel
            control={
              <Checkbox
                checked={captureConfirmed}
                onChange={(e) => setCaptureConfirmed(e.target.checked)}
              />
            }
            label={CAPTURE_CONFIRMATION_LABEL}
          />

          <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Tooltip title={submitBlockedReason(state, captureConfirmed) || ""}>
              <span>
                <Button
                  variant="contained"
                  disabled={submitting || !canSubmitOnboarding(state, captureConfirmed)}
                  onClick={openSubmissionPlan}
                >
                  Submit onboarding
                </Button>
              </span>
            </Tooltip>
            {/* The draft preview is the read-back-to-the-client view (S7.12). */}
            <Button component={Link} to={`/company/${companyRowID}/onboarding/sop`}>
              Preview draft SOP
            </Button>
            {/* The request Imperans will receive, checked before anything is sent (Epic 6303 CRM2). */}
            <Button onClick={onCheckImperansRequest}>Check Imperans request</Button>
          </Box>

          <ActionModal
            open={planOpen}
            title="Submit this onboarding?"
            items={[...(plan?.steps || []), ...(plan?.notifications || [])]}
            note={plan?.turnaroundNote}
            confirmLabel="Submit onboarding"
            busy={submitting}
            onConfirm={submit}
            onCancel={() => setPlanOpen(false)}
          />

          {!state.canSubmit && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Only {emDashWhenBlank(state.primaryRelationshipManager)}, the client's
              primary relationship manager, can submit this onboarding.
            </Alert>
          )}
        </Paper>
      )}

      {importCheckPanel}
    </>
  );
};

/**
 * One line per active testing service, escalation legs included (bug 6364; S2.3–S2.5). A service
 * kept behind an unticked method is not printed, because it is not sent (bug 6371).
 */
const screenedSummary = (state: IOnboardingWizardState): string[] => {
  const services = state.services;
  if (!services) return [];
  const label = (options: any[], key: any) =>
    options?.find((x: any) => x.key === key)?.label ?? key ?? "—";

  return activeServices(services).map((service) => {
    const methodLabel = label(state.references.step2ServiceMethods, service.method);
    const parts = [
      `${service.name || "—"}`,
      `${label(state.references.step2TestReasons, service.testReasonCode)} · ${methodLabel}`,
    ];
    if (service.method !== METHOD_ALCOHOL) {
      parts.push(
        service.collection === COLLECTION_COLLECT_ONLY
          ? "not screened on site"
          : label(state.references.step2ScreeningDevices, service.screeningDevice),
        label(state.references.step2Collections, service.collection),
        label(state.references.step2LabOptions, service.labOption),
        label(state.references.step2LabServices, service.labService),
      );
      if (service.labPanel) parts.push(`lab panel ${label(state.references.step2LabPanels, service.labPanel)}`);
    }
    if (service.syntheticCannabis) parts.push("synthetic cannabis");
    if (service.alcohol && service.method !== METHOD_ALCOHOL) parts.push("alcohol");
    if (service.secondary)
      parts.push(
        `secondary on ${service.method === "Urine" ? "oral fluid" : "urine"} (${label(
          state.references.step2Collections,
          service.secondary.collection,
        )}${service.secondary.syntheticCannabis ? ", synthetic cannabis" : ""})`,
      );
    return parts.join(" · ");
  });
};

const specialistSummary = (state: IOnboardingWizardState): string[] => {
  const services = state.services;
  if (!services) return [];
  const label = (options: any[], key: any) =>
    options?.find((x: any) => x.key === key)?.label ?? key ?? "—";
  const lines: string[] = [];

  const describe = (methodLabel: string, cards: typeof services.hairOccasions) =>
    cards
      .filter((card) => card.included)
      .forEach((card) => {
        const heading = `${methodLabel} · ${label(state.references.step2HairTestReasons, card.occasionCode)} · ${
          card.testType === "TimeOfBooking" ? "Time of Booking" : card.testReason || "—"
        }`;
        if (card.testType === "TimeOfBooking") {
          lines.push(heading);
          return;
        }
        card.serviceLines.forEach((line) =>
          lines.push(
            `${heading} — ${line.serviceName || "—"} · ${line.periodMonths ?? "—"} months · ${label(
              state.references.step2SpecialistPanels,
              line.panelKey,
            )}`,
          ),
        );
      });

  if (services.hairEnabled) describe("Hair", services.hairOccasions);
  if (services.nailEnabled) describe("Nail", services.nailOccasions);
  return lines;
};

const otherServicesSummary = (state: IOnboardingWizardState): string => {
  const other = state.services?.otherServices;
  if (!other) return "";
  const chosen = [
    other.policyDevelopment ? "Policy Development" : null,
    other.training ? "Training" : null,
    other.other ? `Other${other.otherDescription ? `: ${other.otherDescription}` : ""}` : null,
  ].filter(Boolean);
  return chosen.length ? `Other services — ${chosen.join(", ")}` : "";
};

/** Each service with its levels, and each role as its own line (S2.15). */
const healthTickSummary = (state: IOnboardingWizardState): string[] => {
  const healthTick = state.services?.healthTick;
  if (!healthTick) return [];
  const lines: string[] = [];

  /** The laboratory's name rather than its stored key (bug 5717). */
  const labName = (key: string | null | undefined) =>
    referenceLabel(state.references.step2Laboratories, key);

  const describe = (name: string, selected: boolean, levels: string[], roles: any[]) => {
    if (!selected) return;
    lines.push(`${name}${levels.length ? ` — ${levels.join(", ")}` : " — no level chosen"}`);
    roles.forEach((role) =>
      lines.push(
        `   ${role.name || "Unnamed role"}${role.frequency ? ` · ${role.frequency}` : ""} · ${
          (role.tests || []).length
        } tests`,
      ),
    );
  };

  describe("PEM", healthTick.pemSelected, healthTick.pemLevels || [], healthTick.pemRoles || []);
  describe("AHM", healthTick.ahmSelected, healthTick.ahmLevels || [], healthTick.ahmRoles || []);
  if (healthTick.fluSelected) lines.push("Flu Vouchers / Vaccinations");
  if (healthTick.dl9Selected) lines.push("DL9's");
  if (healthTick.htPreEmployment?.included)
    lines.push(
      `HT Pre-employment — ${healthTick.htPreEmployment.method === "OralFluid" ? "oral fluid" : "urine"} · ${
        labName(healthTick.htPreEmployment.confirmationLab) || "no laboratory"
      }`,
    );
  if (healthTick.otherHealthTickServices)
    lines.push(`Other HealthTick services — ${healthTick.otherHealthTickServices}`);
  return lines;
};

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" color="text.secondary">
    {children}
  </Typography>
);

/**
 * Renders a stored rich-text slot. The server filters these values to the allow-list on every write
 * AND again on every read (S6.7), so what arrives here carries no attributes, no script and no
 * disallowed element — the re-filter at render is what makes this safe for values authored by an
 * import, an API caller or a phase-2 client-completed form rather than by our own editor.
 */
const SafeHtml: React.FC<{ html: string | null | undefined }> = ({ html }) => {
  if (!html || !`${html}`.trim())
    return (
      <Typography variant="body2" color="text.secondary">
        Not provided.
      </Typography>
    );
  return (
    <Typography variant="body2" component="div" dangerouslySetInnerHTML={{ __html: html }} />
  );
};

export default OnboardingReviewStep;
